import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import { colors } from "sexy";
import { SyncEmail } from "types/sync_email";
import { getProjectDisplayName } from "types";
import { useProjectStore } from "@store/projectStore";
import { syncEmailsApi } from "@services/api/syncEmails";
import { showNotification } from "context/notification.service";
import { useEffect, useState } from "react";
import { getLocalFromFlutter } from "@services/timeHelpers";

interface InboxEmailModalProps {
  onClose: () => void;
  email: SyncEmail;
}

export const InboxEmailModal = ({ onClose, email }: InboxEmailModalProps) => {
  const projects = useProjectStore((s) => s.projects);
  const markInboxEmailRead = useProjectStore((s) => s.markInboxEmailRead);
  const assignInboxEmail = useProjectStore((s) => s.assignInboxEmail);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [assigning, setAssigning] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  // Mark as read on open
  useEffect(() => {
    if (!email.is_read) {
      markInboxEmailRead(email.id);
    }
  }, [email.id]);

  const handleAssign = async () => {
    if (!selectedProjectId) return;
    setAssigning(true);
    const result = await assignInboxEmail(email.id, selectedProjectId);
    setAssigning(false);
    if (result) {
      showNotification({ type: "success", message: "Email assigné au projet" });
    } else {
      showNotification({ type: "error", message: "Erreur lors de l'assignation" });
    }
  };

  const handleReply = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      await syncEmailsApi.reply(email.id, replyBody);
      showNotification({ type: "success", message: "Réponse envoyée" });
      setShowReply(false);
      setReplyBody("");
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi de la réponse" });
    } finally {
      setSending(false);
    }
  };

  const projectOptions = projects.map((p) => ({
    value: p.id,
    label: getProjectDisplayName(p),
  }));

  return (
    <BaseModal
      width="max-w-800"
      title="Email reçu"
      startIcon={<Icon1 name="inbox" size={20} />}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        {/* Email Headers */}
        <div className="flex flex-col text-h2 gap-3">
          {/* From */}
          <div className="flex flex-row items-center">
            <div className="min-w-100 text-neutral-600">De</div>
            <div className="w-full py-2 font-medium">
              {email.from_name ? `${email.from_name} <${email.from_email}>` : email.from_email}
            </div>
          </div>

          {/* To */}
          <div className="flex flex-row items-start">
            <div className="min-w-100 text-neutral-600">À</div>
            <div className="w-full py-2 font-medium">
              {email.to_emails?.join(", ") || "—"}
            </div>
          </div>

          {/* CC */}
          {email.cc_emails && email.cc_emails.length > 0 && (
            <div className="flex flex-row items-start">
              <div className="min-w-100 text-neutral-600">CC</div>
              <div className="w-full py-2 font-medium">
                {email.cc_emails.join(", ")}
              </div>
            </div>
          )}

          {/* Date */}
          <div className="flex flex-row items-start">
            <div className="min-w-100 text-neutral-600">Date</div>
            <div className="w-full py-2 font-medium">
              {email.received_at ? getLocalFromFlutter(email.received_at) : "Date inconnue"}
            </div>
          </div>

          {/* Subject */}
          <div
            className="rounded-lg p-3 mt-2"
            style={{ backgroundColor: colors.neutral_50 }}
          >
            <div className="text-sm font-semibold">
              {email.subject || "(Sans objet)"}
            </div>
          </div>
        </div>

        {/* Body */}
        <div
          className="rounded-lg p-3 mt-2"
          style={{ backgroundColor: colors.neutral_50 }}
        >
          <div className="text-sm whitespace-pre-wrap leading-5">
            {email.body_text || "(Aucun contenu texte)"}
          </div>
        </div>

        {/* Attachments */}
        {email.attachments && email.attachments.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-2">
            <div className="text-xs text-neutral-400">
              Pièces jointes ({email.attachments.length})
            </div>
            {email.attachments.map((file, index) => (
              <div
                key={index}
                className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg"
                style={{ backgroundColor: colors.neutral_50 }}
              >
                <Icon1 name="join" size={14} color={colors.neutral_500} />
                <span className="text-sm text-neutral-600 truncate">{file.name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Project association */}
        {email.project_id ? (
          <div className="flex flex-row items-center gap-2 mt-3 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
            <Icon1 name="cube" size={14} color="#1E40AF" />
            <span className="text-sm text-blue-800">
              Projet : {(() => { const p = projects.find((p) => p.id === email.project_id); return p ? getProjectDisplayName(p) : email.project_id; })()}
            </span>
            {email.matched_via && (
              <span className="text-xs text-blue-500 ml-auto">
                {email.matched_via === "reply_to" && "Auto (réponse)"}
                {email.matched_via === "in_reply_to" && "Auto (en-tête)"}
                {email.matched_via === "manual" && "Manuel"}
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2 mt-3">
            <div className="text-xs text-neutral-400">Aucun projet associé</div>
            <div className="flex flex-row gap-2 items-end">
              <div className="flex-1">
                <DropDown
                  label="Assigner un projet"
                  placeholder="Sélectionner un projet..."
                  options={projectOptions}
                  value={selectedProjectId}
                  onSelect={(v) => setSelectedProjectId(v as string)}
                />
              </div>
              {selectedProjectId && (
                <Button
                  label="Assigner"
                  onClick={handleAssign}
                  disabled={assigning}
                  loading={assigning}
                  variant="small"
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Reply form */}
      {showReply && (
        <div className="border-t px-4 pt-3 pb-1 flex flex-col gap-2">
          <div className="text-h3 font-medium text-neutral-600">
            Répondre à {email.from_name || email.from_email}
          </div>
          <AreaTextField
            value={replyBody}
            onChange={setReplyBody}
            placeholder="Votre réponse..."
            rows={6}
          />
          <div className="flex flex-row justify-end gap-2">
            <SecondaryButton variant="small" onClick={() => { setShowReply(false); setReplyBody(""); }}>
              Annuler
            </SecondaryButton>
            <Button
              variant="small"
              label={sending ? "Envoi..." : "Envoyer"}
              onClick={handleReply}
              disabled={sending || !replyBody.trim()}
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        {!showReply && (
          <Button
            variant="small"
            label="Répondre"
            onClick={() => setShowReply(true)}
          />
        )}
        <SecondaryButton variant="small" onClick={onClose}>
          Fermer
        </SecondaryButton>
      </div>
    </BaseModal>
  );
};
